import { t } from '@/i18n';

import type { FinancialEventImpact } from '@/entities/financial-calendar';

/**
 * 중요도 → 색 토큰(+ 과거 3분할 바의 활성 칸 수, 아직 참조하는 소비처가 남아 있어 필드
 * 자체는 유지한다 — 아래 TRAD-2909 주석 참고).
 *
 * 색은 `--calendar-impact-*` — **등락 토큰(`--price-rise/fall`)과 분리된 축**이다.
 * 중요도에 빨강을 쓰면 KR 관례(빨강=상승)와 충돌해 "중요도 높음"이 상승 신호로 읽힌다.
 * 실제로 기존 HIGH(`text-red-500`)는 상세 패널의 하락 값과 동일 RGB로 렌더됐다.
 *
 * TRAD-2127 — 한 등급당 3개 채널(text/bg/border)을 함께 들고 있다. 캘린더 그리드가
 * 막대(bg)와 좌측 경계선(border)을 쓰는데, 예전엔 그 두 곳이 토큰을 못 쓰고 생 hex 를
 * 인라인 style 로 박고 있었다(= 다크모드가 아예 닿지 않는 구조).
 *
 * 🔴 TRAD-2909(§K-2) — **3분할 바 컴포넌트(`ImpactBar`) 자체는 폐기했다.** 근거는 designer
 * §K-1(운영 전수 17,038건, 어느 필터 체제에서도 바가 ~95% 상수를 그려 정보량
 * 0.22~0.31 bit) + 인접 등급 색 구분 불가(HIGH 4.31:1/MEDIUM 3.69:1/LOW 3.12:1, 셋 다
 * WCAG 4.5 미만). 목록·상세·격자칩 4곳은 `ImportanceBadge`(HIGH 전용 텍스트)로
 * 전환했다 — 이 파일이 통째로 안 지워진 이유는 아래를 보라.
 *
 * 🔴 **파일 전체 삭제(designer 집행 순서 5번)가 이번엔 안 됐다.** designer의 소비처
 * grep 은 `<ImpactBar` **태그** 기준이라, 같은 저대비 토큰을 태그 없이 직접 참조하는
 * 두 곳을 못 잡았다(`calendar-event-content.tsx`):
 *   ① 주뷰 이벤트 칩의 좌측 `border-l-[3px]` 색(`impactClasses(...).border`, 3등급 전부)
 *   ② 월뷰 이벤트의 6px 점 색(`impactClasses(...).bar`, 3등급 전부)
 * 둘 다 이 `IMPACT_CONFIG`를 직접 참조해서 살아있다 — team-lead에게 별도 보고했다
 * (TRAD-2909 커밋 메시지 참고), 여기서 임의로 정리하지 않는다. 그래서 `IMPACT_CONFIG`·
 * `NEUTRAL_IMPACT_CLASS`·`impactAriaLabel` 3개는 계속 남기고, 3분할 바 렌더링에만
 * 쓰이던 `ImpactBar` 컴포넌트·`TOTAL_SEGMENTS`·`INACTIVE_BAR_CLASS`·크기 프리셋만
 * 죽은 코드로 확인돼 지웠다(전수 grep, `__tests__` 제외 소비처 0건).
 */
export const IMPACT_CONFIG = {
  HIGH: {
    segments: 3,
    color: 'text-calendar-impact-high',
    bar: 'bg-calendar-impact-high',
    border: 'border-calendar-impact-high',
    labelKey: 'financial_calendar.impact.high',
  },
  MEDIUM: {
    segments: 2,
    color: 'text-calendar-impact-medium',
    bar: 'bg-calendar-impact-medium',
    border: 'border-calendar-impact-medium',
    labelKey: 'financial_calendar.impact.medium',
  },
  LOW: {
    segments: 1,
    color: 'text-calendar-impact-low',
    bar: 'bg-calendar-impact-low',
    border: 'border-calendar-impact-low',
    labelKey: 'financial_calendar.impact.low',
  },
} as const satisfies Record<
  FinancialEventImpact,
  { segments: number; color: string; bar: string; border: string; labelKey: string }
>;

/** impact 가 없는 이벤트의 중립 경계선/점 — 활성 램프와 헷갈리지 않게 회색 토큰. */
export const NEUTRAL_IMPACT_CLASS = { bar: 'bg-grey-40', border: 'border-grey-40' } as const;

/**
 * 스크린리더용 라벨(주뷰 격자 칩·월뷰 점에서 계속 쓴다 — `ImportanceBadge`는 텍스트
 * 자체가 콘텐츠라 별도 aria-label이 필요 없다).
 *
 * ⚠️ 식별자와 라벨은 일치시키지 않는다. 코드·데이터는 서버 계약을 따라 `impact`,
 * 사용자 노출 문구는 각 시장 관례를 따른다 — KR `중요도`, EN `Importance`.
 *
 * 키는 `IMPACT_CONFIG.labelKey` 에서 정적으로 가져온다. 템플릿 리터럴로 키를 조립하면
 * `t()` 의 키 리터럴 유니온 타입을 벗어나 **카탈로그에 없는 키도 컴파일을 통과**한다.
 */
export const impactAriaLabel = (impact: FinancialEventImpact): string =>
  `${t('financial_calendar.impact.label')}: ${t(IMPACT_CONFIG[impact].labelKey)}`;
